import { useEffect, useCallback } from 'react';
import { getSocket } from '../lib/socket';

export const useSocket = () => {
  const socket = getSocket();

  const on = useCallback((event: string, handler: (...args: any[]) => void) => {
    socket.on(event, handler);
    return () => {
      socket.off(event, handler);
    };
  }, [socket]);

  const emit = useCallback((event: string, ...args: any[]) => {
    socket.emit(event, ...args);
  }, [socket]);

  const off = useCallback((event: string, handler?: (...args: any[]) => void) => {
    if (handler) {
      socket.off(event, handler);
    } else {
      socket.off(event);
    }
  }, [socket]);

  return {
    socket,
    on,
    emit,
    off,
    connected: socket.connected,
  };
};

// Hook for listening to metric updates
export const useMetricUpdates = (callback: (data: any) => void) => {
  const { on } = useSocket();

  useEffect(() => {
    const cleanup = on('metric:updated', callback);
    return cleanup;
  }, [on, callback]);
};

// Hook for listening to report progress
export const useReportProgress = (callback: (data: any) => void) => {
  const { on } = useSocket();

  useEffect(() => {
    const cleanup = on('report:progress', callback);
    return cleanup;
  }, [on, callback]);
};

// Hook for listening to report ready events
export const useReportReady = (callback: (data: any) => void) => {
  const { on } = useSocket();

  useEffect(() => {
    const cleanup = on('report:ready', callback);
    return cleanup;
  }, [on, callback]);
};

// Hook for listening to agent analysis complete
export const useAgentAnalysisComplete = (callback: (data: any) => void) => {
  const { on } = useSocket();

  useEffect(() => {
    const cleanup = on('agent:analysis:complete', callback);
    return cleanup;
  }, [on, callback]);
};

// Hook for listening to achievement unlocks
export const useAchievementUnlock = (callback: (data: any) => void) => {
  const { on } = useSocket();

  useEffect(() => {
    const cleanup = on('achievement:unlocked', callback);
    return cleanup;
  }, [on, callback]);
};
